/**
 * Central registry for document print layouts.
 * Add / update a formKey here when print support is added — Documents UI stays thin.
 *
 * Each entry:
 *  - getPath(detailId, viewerUserId?) → API path
 *  - parse(response, targetUserId) → domain record
 *  - build(record, branding) → full HTML document
 *  - needsSeniorStaffNurse?: boolean
 */
import {
  buildReferenceBackgroundCheckPrintHtml,
  parseConsentFromApiResponse,
} from "./forms/referenceBackgroundCheckPrint";
import {
  buildApplicationFormPrintHtml,
  parseApplicationFromApiResponse,
} from "./forms/applicationFormPrint";
import {
  buildWelcomeLetterPrintHtml,
  parseWelcomeLetterFromApiResponse,
} from "./forms/welcomeLetterPrint";
import {
  buildConfidentialityAgreementPrintHtml,
  parseConfidentialityAgreementFromApiResponse,
} from "./forms/confidentialityAgreementPrint";
import {
  buildEmploymentAgreementPrintHtml,
  parseEmploymentAgreementFromApiResponse,
} from "./forms/employmentAgreementPrint";
import {
  buildStateMandateCompliancePrintHtml,
  parseStateMandateComplianceFromApiResponse,
} from "./forms/stateMandateCompliancePrint";
import {
  buildJobDescriptionPrintHtml,
  parseJobDescriptionFromApiResponse,
} from "./forms/jobDescriptionPrint";
import {
  buildRolesAndResponsibilitiesPrintHtml,
  parseRolesAndResponsibilitiesFromApiResponse,
} from "./forms/rolesAndResponsibilitiesPrint";
import {
  buildPostTestPrintHtml,
  parsePostTestFromApiResponse,
} from "./forms/postTestPrint";
import {
  buildChhaCompetencyTestPrintHtml,
  parseChhaCompetencyTestFromApiResponse,
} from "./forms/chhaCompetencyTestPrint";
import {
  buildContractorServiceAgreementPrintHtml,
  parseContractorFromApiResponse,
} from "./forms/contractorServiceAgreementPrint";
import {
  buildAdministrativeOrientationPrintHtml,
  parseAdministrativeOrientationFromApiResponse,
} from "./forms/administrativeOrientationPrint";
import {
  buildOrientationChecklistPrintHtml,
  parseOrientationChecklistFromApiResponse,
} from "./forms/orientationChecklistPrint";
import {
  buildHandbookFormPrintHtml,
  parseHandbookFormFromApiResponse,
} from "./forms/handbookFormPrint";
import {
  buildIdentificationCardPrintHtml,
  parseIdentificationCardFromApiResponse,
} from "./forms/identificationCardPrint";
import {
  buildHepatitisBPolicyPrintHtml,
  parseHepatitisBPolicyFromApiResponse,
} from "./forms/hepatitisBPolicyPrint";
import {
  buildCompetencyPrintHtml,
  parseCompetencyFromApiResponse,
} from "./forms/competencyPrint";
import {
  buildTbTestPrintHtml,
  parseTbTestFromApiResponse,
} from "./forms/tbTestPrint";
import {
  buildFluVaccinationPrintHtml,
  parseFluVaccinationFromApiResponse,
} from "./forms/fluVaccinationPrint";
import {
  buildEmployeeHealthExaminationPrintHtml,
  parseEmployeeHealthExaminationFromApiResponse,
} from "./forms/employeeHealthExaminationPrint";

/** @typedef {{
 *   getPath: (detailId: string|number, viewerUserId?: string|number|null) => string,
 *   parse: (response: unknown, targetUserId?: string|number) => unknown,
 *   build: (record: any, branding: Record<string, string>) => string,
 *   needsSeniorStaffNurse?: boolean,
 *   label?: string,
 * }} PrintFormConfig */

/**
 * @param {string} basePath
 * @returns {PrintFormConfig['getPath']}
 */
function pathWithOptionalUser(basePath) {
  return (detailId, viewerUserId) =>
    viewerUserId != null && String(viewerUserId).trim() !== ""
      ? `${basePath}/${detailId}?userId=${viewerUserId}`
      : `${basePath}/${detailId}`;
}

/** @type {Record<string, PrintFormConfig>} */
export const DOCUMENT_PRINT_REGISTRY = {
  employment_application: {
    label: "Application form",
    getPath: pathWithOptionalUser("/employment-applications"),
    parse: parseApplicationFromApiResponse,
    build: buildApplicationFormPrintHtml,
  },
  reference_background_check: {
    label: "Reference & Background Check",
    getPath: pathWithOptionalUser("/reference-background-consents"),
    parse: parseConsentFromApiResponse,
    build: buildReferenceBackgroundCheckPrintHtml,
  },
  welcome_letter: {
    label: "Welcome Letter",
    getPath: pathWithOptionalUser("/welcome-letters"),
    parse: parseWelcomeLetterFromApiResponse,
    build: buildWelcomeLetterPrintHtml,
  },
  confidentiality_agreement: {
    label: "Confidentiality Agreement",
    getPath: pathWithOptionalUser("/confidentiality-agreements"),
    parse: parseConfidentialityAgreementFromApiResponse,
    build: buildConfidentialityAgreementPrintHtml,
  },
  employment_agreement: {
    label: "Employment Agreement",
    getPath: pathWithOptionalUser("/employment-agreements"),
    parse: parseEmploymentAgreementFromApiResponse,
    build: buildEmploymentAgreementPrintHtml,
  },
  state_mandate_compliance: {
    label: "State Mandate Compliance",
    getPath: pathWithOptionalUser("/state-mandate-compliances"),
    parse: parseStateMandateComplianceFromApiResponse,
    build: buildStateMandateCompliancePrintHtml,
    needsSeniorStaffNurse: true,
  },
  job_description: {
    label: "Job Description",
    getPath: pathWithOptionalUser("/job-descriptions"),
    parse: parseJobDescriptionFromApiResponse,
    build: buildJobDescriptionPrintHtml,
    needsSeniorStaffNurse: true,
  },
  roles_and_responsibilities: {
    label: "Roles and Responsibilities",
    getPath: pathWithOptionalUser("/roles-and-responsibilities"),
    parse: parseRolesAndResponsibilitiesFromApiResponse,
    build: buildRolesAndResponsibilitiesPrintHtml,
  },
  post_test: {
    label: "Post Test",
    getPath: pathWithOptionalUser("/post-tests"),
    parse: parsePostTestFromApiResponse,
    build: buildPostTestPrintHtml,
  },
  chha_competency_test: {
    label: "CHHA Competency Test",
    getPath: pathWithOptionalUser("/chha-competency-tests"),
    parse: parseChhaCompetencyTestFromApiResponse,
    build: buildChhaCompetencyTestPrintHtml,
  },
  contractor: {
    label: "Contractor Service Agreement",
    getPath: pathWithOptionalUser("/contractors"),
    parse: parseContractorFromApiResponse,
    build: buildContractorServiceAgreementPrintHtml,
  },
  contractor_agreement: {
    label: "Contractor Service Agreement",
    getPath: pathWithOptionalUser("/contractors"),
    parse: parseContractorFromApiResponse,
    build: buildContractorServiceAgreementPrintHtml,
  },
  contractor_form: {
    label: "Contractor Service Agreement",
    getPath: pathWithOptionalUser("/contractors"),
    parse: parseContractorFromApiResponse,
    build: buildContractorServiceAgreementPrintHtml,
  },
  administrative_orientation: {
    label: "Administrative Orientation",
    getPath: pathWithOptionalUser("/administrative-orientations"),
    parse: parseAdministrativeOrientationFromApiResponse,
    build: buildAdministrativeOrientationPrintHtml,
  },
  orientation_checklist: {
    label: "Orientation Checklist",
    getPath: pathWithOptionalUser("/orientation-checklists"),
    parse: parseOrientationChecklistFromApiResponse,
    build: buildOrientationChecklistPrintHtml,
  },
  handbook_form: {
    label: "Handbook acknowledgement",
    getPath: pathWithOptionalUser("/handbooks"),
    parse: parseHandbookFormFromApiResponse,
    build: buildHandbookFormPrintHtml,
  },
  identification_card: {
    label: "Identification card",
    getPath: pathWithOptionalUser("/identification-cards"),
    parse: parseIdentificationCardFromApiResponse,
    build: buildIdentificationCardPrintHtml,
  },
  hepatitis_form: {
    label: "Hepatitis B policy",
    getPath: pathWithOptionalUser("/hepatitis-b-policy"),
    parse: parseHepatitisBPolicyFromApiResponse,
    build: buildHepatitisBPolicyPrintHtml,
  },
  competency: {
    label: "Competency assessment",
    getPath: pathWithOptionalUser("/competencies"),
    parse: parseCompetencyFromApiResponse,
    build: buildCompetencyPrintHtml,
  },
  tb_test_form: {
    label: "TB screening",
    getPath: pathWithOptionalUser("/tb-tests"),
    parse: parseTbTestFromApiResponse,
    build: buildTbTestPrintHtml,
  },
  flu_vaccination_program: {
    label: "Flu vaccination",
    getPath: pathWithOptionalUser("/flu-vaccinations"),
    parse: parseFluVaccinationFromApiResponse,
    build: buildFluVaccinationPrintHtml,
  },
  employee_health_examination: {
    label: "Employee health examination",
    getPath: pathWithOptionalUser("/employee-health-examinations"),
    parse: parseEmployeeHealthExaminationFromApiResponse,
    build: buildEmployeeHealthExaminationPrintHtml,
  },
};

/**
 * @param {string|undefined|null} formKey
 * @returns {PrintFormConfig|null}
 */
export function getPrintConfig(formKey) {
  if (!formKey) return null;
  return DOCUMENT_PRINT_REGISTRY[formKey] || null;
}

/**
 * @param {string|undefined|null} formKey
 */
export function isPrintableFormKey(formKey) {
  return Boolean(getPrintConfig(formKey)) || formKey === "evaluation";
}
